/**
 *  AppShell script file
**/
import { ACTIONS, MUTATIONS, GETTERS } from '@/store'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { MapComponent } from '@/components/map-component'
import { Layers } from '@/components/layers'
import { AenaPoints } from '@/components/aena-points'
import { AenaTerminalSelector } from '@/components/aena-terminal-selector'
import { AenaHomeButton } from '@/components/aena-home-button'
import { AenaLegend } from '@/components/aena-legend'
import { AenaPmr } from '@/components/aena-pmr'
import { Feedback } from '@/components/feedback'
import saveActions from '@/mixins/saveActions'
import { isMobile } from '@/utils/isMobile'
import setDeviceWidthClass from '@/mixins/setDeviceWidthClass'

const TIME_TO_RETURN_HOME_ON_MUPI_MODE = 120000

export default {
  name: 'app-appShell',
  mixins: [saveActions, setDeviceWidthClass],
  components: {
    MapComponent,
    AenaPoints,
    AenaTerminalSelector,
    AenaHomeButton,
    AenaLegend,
    AenaPmr,
    Feedback,
    Layers
  },
  props: {},
  data () {
    return {
      loaded: false,
      id: this.$root.$data.id,
      airport: this.$root.$data.airport,
      building: this.$root.$data.building,
      floor: this.$root.$data.floor,
      explorerMode: this.$root.$data.explorerMode,
      category: this.$root.$data.category,
      poi: (this.$root.$data.poi || '').toString(),
      poiD: (this.$root.$data.poiD || '').toString(),
      route: this.$root.$data.route,
      language: this.$root.$data.language,
      isPortrait: null,
      pmr_enabled: process.env.VUE_APP_PMR_MODE,
      interval: undefined,
      intervalPoiWait: undefined,
      container: null,
      printMode: this.$root.$data.printMode,
      layers: false,
      lat: this.$root.$data.lat,
      lon: this.$root.$data.lon,
      mupiMode: this.$root.$data.mupiMode,
      avaMode: this.$root.$data.avaMode,
      subcategories: this.$root.$data.subcategories,
      zonaubicacion: this.$root.$data.zonaubicacion,
      filtering: this.$root.$data.filtering,
      intervalMupiResetPosition: undefined,
      pointsDetailCollapsed: true
    }
  },
  async created () {
    document.documentElement.setAttribute('lang', this.language)
    this.initFeedback()
    this.saveConfig()
    this[ACTIONS.LOAD_FULL_AIRPORT]().then(() => {
      if (this.building) {
        this.saveFloor({ buildingId: this.building, floorId: this.floor, airportID: this.airport, tag: 'explorador' })
      }

      if (this.airportC.access_controls_has_wait_info) {
        this[ACTIONS.LOAD_ACCESS_CONTROL_TIME]({ code: this.airport, pois: this.pois })

        this.intervalPoiWait = setInterval(() => {
          this[ACTIONS.LOAD_ACCESS_CONTROL_TIME]({ code: this.airport, pois: this.pois })
        }, 300000)
      }

      this.loaded = true
    })

    window.addEventListener('orientationchange', this.handleOrientation, true)
    this.handleOrientation()
    window.addEventListener('resize', this.checkIsMobile, true)

    document.addEventListener('keydown', this.addAccesibilityClass, true)
    document.addEventListener('mousedown', this.removeAccesibilityClass, true)
    if (this.mupiMode) {
      this[ACTIONS.CLEAR_ROUTE]()
      this.initMupiIntervalEvents()
    }
  },
  mounted () {
    this.container = document.querySelector('.flughaveno-map')

    this.checkIsMobile()
  },
  beforeDestroy () {
    clearInterval(this.interval)
    clearInterval(this.intervalPoiWait)
    if (this.mupiMode) {
      clearInterval(this.intervalMupiResetPosition)
    }
    window.removeEventListener('resize', this.checkIsMobile, false)
    this.removeMupiInternalEvents()
  },
  computed: {
    ...mapGetters({
      airportC: GETTERS.GET_AIRPORT,
      pois: GETTERS.GET_POIS_LIST,
      selectedPoi: GETTERS.GET_POI,
      buildings: GETTERS.GET_BUILDINGS,
      buildingC: GETTERS.GET_BUILDING,
      floorC: GETTERS.GET_FLOOR,
      routeConfig: GETTERS.GET_ROUTE_CONFIG,
      isRouteMode: GETTERS.IS_ROUTE_MODE,
      feedback: GETTERS.GET_FEEDBACK,
      layerTree: GETTERS.GET_LAYER_TREE,
      filteringC: GETTERS.GET_FILTERING
    }),
    appId () {
      return this.id.split('#')[1]
    },
    hasRoute () {
      return this.airportC.has_graph
    }
  },
  methods: {
    ...mapActions([
      ACTIONS.LOAD_FULL_AIRPORT,
      ACTIONS.SET_FEEDBACK,
      ACTIONS.LOAD_ACCESS_CONTROL_TIME,
      ACTIONS.SET_HOME_POSITION,
      ACTIONS.CLEAR_ROUTE,
      ACTIONS.CLEAR_POI
    ]),
    ...mapMutations([
      MUTATIONS.SAVE_DEVICE_WIDTH,
      MUTATIONS.SAVE_IS_MOBILE,
      MUTATIONS.SAVE_FILTERING
    ]),
    resize () {
      this[MUTATIONS.SAVE_DEVICE_WIDTH](this.$el.offsetWidth)
    },
    saveConfig () {
      this.$store.commit(MUTATIONS.SAVE_CONFIG, {
        id: this.id,
        airport: this.airport,
        explorerMode: this.explorerMode,
        building: this.building,
        floor: this.floor,
        category: this.category,
        subcategories: this.subcategories,
        language: this.language,
        route: this.route,
        poi: this.poi,
        poiD: this.poiD,
        lat: this.lat,
        lon: this.lon,
        mupiMode: this.mupiMode,
        zonaubicacion: this.zonaubicacion
      })

      const filtering = this.filteringC != null ? this.filteringC : this.filtering

      this.$store.commit(MUTATIONS.SAVE_FILTERING, filtering)
    },
    checkIsMobile () {
      const response = isMobile(this.id)
      this.filtering = this.filteringC || response
      this.resize()
      this[MUTATIONS.SAVE_IS_MOBILE](response)
      this.$store.commit(MUTATIONS.SAVE_FILTERING, this.filtering)
    },
    handleOrientation () {
      window.setTimeout(() => {
        if (window.orientation === 0 && window.innerWidth <= 768 && window.innerWidth >= 570) {
          this.isPortrait = true
        } else {
          this.isPortrait = false
        }
      }, 5000)
    },
    closeModal () {
      this.isPortrait = false
    },
    initFeedback () {
      const resetFeedback = () => {
        const feedback = JSON.parse(localStorage.getItem('feedback') || '{}')
        localStorage.removeItem('feedbacks')
        const now = new Date()

        if (!feedback.time || new Date(feedback.time).getUTCDate() !== now.getUTCDate()) {
          feedback.time = now.toISOString()
          feedback.count = 1
          feedback.answered = false
          localStorage.setItem('feedback', JSON.stringify(feedback))
        }
      }
      resetFeedback()

      this.interval = setInterval(() => {
        const feedback = JSON.parse(localStorage.getItem('feedback') || '{}')
        clearInterval(this.interval)
        if (feedback.count < 5) {
          feedback.count = 4
        }
        localStorage.setItem('feedback', JSON.stringify(feedback))
        this[ACTIONS.SET_FEEDBACK]()
      }, 600000)
    },
    initMupiIntervalEvents () {
      document.addEventListener('keydown', this.cancelIntervalMupi, false)
      document.addEventListener('keypress', this.cancelIntervalMupi, false)
      document.addEventListener('keyup', this.cancelIntervalMupi, false)
      document.addEventListener('mousedown', this.cancelIntervalMupi, false)
      document.addEventListener('mouseup', this.cancelIntervalMupi, false)
      document.addEventListener('click', this.cancelIntervalMupi, false)
      document.addEventListener('touchstart', this.cancelIntervalMupi, false)
      document.addEventListener('touchend', this.cancelIntervalMupi, false)
      document.addEventListener('touchcancel', this.cancelIntervalMupi, false)
      document.addEventListener('touchleave', this.cancelIntervalMupi, false)
      document.addEventListener('touchmove', this.cancelIntervalMupi, false)

      this.initIntervalMupi()
    },
    removeMupiInternalEvents () {
      document.removeEventListener('keydown', this.cancelIntervalMupi, false)
      document.removeEventListener('keypress', this.cancelIntervalMupi, false)
      document.removeEventListener('keyup', this.cancelIntervalMupi, false)
      document.removeEventListener('mousedown', this.cancelIntervalMupi, false)
      document.removeEventListener('mouseup', this.cancelIntervalMupi, false)
      document.removeEventListener('click', this.cancelIntervalMupi, false)
      document.removeEventListener('touchstart', this.cancelIntervalMupi, false)
      document.removeEventListener('touchend', this.cancelIntervalMupi, false)
      document.removeEventListener('touchcancel', this.cancelIntervalMupi, false)
      document.removeEventListener('touchleave', this.cancelIntervalMupi, false)
      document.removeEventListener('touchmove', this.cancelIntervalMupi, false)
    },
    initIntervalMupi () {
      this.intervalMupiResetPosition = setInterval(() => {
        this[ACTIONS.CLEAR_ROUTE]().then(() => {
          this[ACTIONS.SET_HOME_POSITION](this.poi)
        })
        clearInterval(this.intervalMupiResetPosition)
      }, TIME_TO_RETURN_HOME_ON_MUPI_MODE)
    },
    cancelIntervalMupi (event) {
      clearInterval(this.intervalMupiResetPosition)
      this.intervalMupiResetPosition = undefined
      this.initIntervalMupi()
    },
    addAccesibilityClass (event) {
      if (!this.container.classList.contains('keyboard-nav') && event.key === 'Tab') {
        this.container.classList.add('keyboard-nav')
      }
    },
    removeAccesibilityClass () {
      this.container.classList.remove('keyboard-nav')
    },
    onLayerChange (data) {
      const layer = this.$refs.map.getLayer(data.name)
      this.$refs.map.setLayoutProperty(layer.id, layer.visibility === 'none' ? 'visible' : 'none')
    },
    onPointsToggle (value) {
      this.pointsDetailCollapsed = value
    }
  },
  watch: {
    async floorC () {
      this.floor = (this.floorC || {}).id
      this.building = (this.buildingC || {}).id
      this.saveConfig()
    },
    layerTree (value) {
      const _layers = value.map((layer) => {
        const _layer = layer
        _layer.name = layer.id === 'poiList' ? 'Puntos de interés'
          : layer.id === 'building-contour' ? 'Contorno de edificios'
            : layer.id === 'building-contour-marker' ? 'Marcadores de planta'
              : layer.id === 'poiListSelected' ? 'POIs seleccionados'
                : layer.id === 'spaces_base_fill' ? 'Relleno del espacio'
                  : layer.id
        _layer.active = true
        return _layer
      })
      this.layers = _layers
    }
  }
}
